import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
    Save, 
    ArrowLeft, 
    Briefcase,
    Building2,
    MapPin,
    DollarSign,
    AlignLeft
} from 'lucide-react';
import { useCreateJobMutation, useUpdateJobMutation, useGetJobByIdQuery } from '../../store/slices/apiSlice';

const AdminJobForm = () => {
    const { id } = useParams();
    const isEditing = !!id;
    const navigate = useNavigate();

    const { data: jobData, isLoading: isLoadingJob } = useGetJobByIdQuery(Number(id), {
        skip: !isEditing
    });

    const [createJob, { isLoading: isCreating }] = useCreateJobMutation();
    const [updateJob, { isLoading: isUpdating }] = useUpdateJobMutation();

    const [formData, setFormData] = useState({
        title: '',
        location: '',
        type: 'Full-time' as 'Full-time' | 'Part-time' | 'Contract' | 'Internship',
        department: '',
        salary: '',
        shortDescription: '',
        fullDescription: '',
        responsibilities: '',
        requirements: '',
        isActive: true
    });

    const [error, setError] = useState('');

    useEffect(() => {
        if (isEditing && jobData?.data) {
            setFormData({
                title: jobData.data.title || '',
                location: jobData.data.location || '',
                type: jobData.data.type || 'Full-time',
                department: jobData.data.department || '',
                salary: jobData.data.salary || '',
                shortDescription: jobData.data.shortDescription || '',
                fullDescription: jobData.data.fullDescription || '',
                responsibilities: jobData.data.responsibilities || '',
                requirements: jobData.data.requirements || '',
                isActive: jobData.data.isActive ?? true
            });
        }
    }, [isEditing, jobData]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        try {
            if (isEditing) {
                await updateJob({ id: Number(id), data: formData }).unwrap();
            } else {
                await createJob(formData).unwrap();
            }
            navigate('/admin/jobs');
        } catch (err: any) {
            setError(err.data?.message || 'An error occurred while saving the job.');
        }
    };

    if (isEditing && isLoadingJob) {
        return (
            <div className="p-6">
                <div className="flex justify-center p-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50/50 pb-12">
            <div className="p-6 max-w-5xl mx-auto">
                <div className="flex items-center gap-4 mb-8">
                    <Link 
                        to="/admin/jobs"
                        className="p-2 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                            {isEditing ? 'Edit Job Opening' : 'Post New Job'}
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            {isEditing ? 'Update the details for this job listing.' : 'Create a new job opportunity for candidates.'}
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Info */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="border-b border-gray-100 bg-gray-50/50 px-6 py-4 flex items-center gap-3">
                            <Briefcase className="w-5 h-5 text-blue-500" />
                            <h3 className="font-semibold text-gray-900">Basic Information</h3>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="col-span-1 md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Job Title *</label>
                                <input 
                                    type="text" 
                                    name="title"
                                    required
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="e.g. Senior Frontend Developer"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                                    <MapPin className="w-4 h-4 text-gray-400" /> Location *
                                </label>
                                <input 
                                    type="text" 
                                    name="location"
                                    required
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    placeholder="e.g. Remote, Lagos, London"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                                    <Briefcase className="w-4 h-4 text-gray-400" /> Employment Type *
                                </label>
                                <select 
                                    name="type"
                                    required
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white appearance-none transition-all text-sm"
                                >
                                    <option value="Full-time">Full-time</option>
                                    <option value="Part-time">Part-time</option>
                                    <option value="Contract">Contract</option>
                                    <option value="Internship">Internship</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                                    <Building2 className="w-4 h-4 text-gray-400" /> Department
                                </label>
                                <input 
                                    type="text" 
                                    name="department"
                                    value={formData.department}
                                    onChange={handleInputChange}
                                    placeholder="e.g. Engineering, Marketing"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                                    <DollarSign className="w-4 h-4 text-gray-400" /> Salary Range (Optional)
                                </label>
                                <input 
                                    type="text" 
                                    name="salary"
                                    value={formData.salary}
                                    onChange={handleInputChange}
                                    placeholder="e.g. $50,000 - $80,000"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                />
                            </div>
                            
                            <div className="col-span-1 md:col-span-2">
                                <label className="flex items-center gap-3 cursor-pointer p-4 border border-gray-200 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition-colors w-max">
                                    <input 
                                        type="checkbox" 
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-900">
                                        Active (Visible to public)
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Description Details */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="border-b border-gray-100 bg-gray-50/50 px-6 py-4 flex items-center gap-3">
                            <AlignLeft className="w-5 h-5 text-blue-500" />
                            <h3 className="font-semibold text-gray-900">Job Description</h3>
                        </div>
                        <div className="p-6 space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Short Summary (For job cards) *
                                </label>
                                <textarea 
                                    name="shortDescription"
                                    required
                                    rows={3}
                                    value={formData.shortDescription}
                                    onChange={handleInputChange}
                                    placeholder="A brief 1-2 sentence overview of the role..."
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Description *
                                </label>
                                <textarea 
                                    name="fullDescription"
                                    required
                                    rows={6}
                                    value={formData.fullDescription}
                                    onChange={handleInputChange}
                                    placeholder="Detailed overview of the role, team, and company..."
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Key Responsibilities (One per line)
                                </label>
                                <textarea 
                                    name="responsibilities"
                                    rows={6}
                                    value={formData.responsibilities}
                                    onChange={handleInputChange}
                                    placeholder="- Daily task 1&#10;- Daily task 2"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm font-mono"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Requirements / Qualifications (One per line)
                                </label>
                                <textarea 
                                    name="requirements"
                                    rows={6}
                                    value={formData.requirements}
                                    onChange={handleInputChange}
                                    placeholder="- 3+ years experience&#10;- B.Sc in related field"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm font-mono"
                                />
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-50 text-red-600 rounded-xl text-sm border border-red-100">
                            {error}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-4 pt-4">
                        <Link 
                            to="/admin/jobs"
                            className="px-6 py-3 bg-white text-gray-700 hover:bg-gray-50 rounded-xl font-medium border border-gray-200 transition-colors"
                        >
                            Cancel
                        </Link>
                        <button 
                            type="submit"
                            disabled={isCreating || isUpdating}
                            className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-bold shadow-lg shadow-blue-200 flex items-center gap-2 disabled:opacity-50"
                        >
                            {isCreating || isUpdating ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    <Save className="w-5 h-5" />
                                    {isEditing ? 'Save Changes' : 'Post Job'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminJobForm;
